import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Download,
  ImagePlus,
  PenLine,
  Settings,
  Trash2,
  Upload,
  Users,
  X,
} from 'lucide-react';
import type {
  EmailSignature,
  MarketingEmailContact,
  MarketingEmailSettings,
} from '../../../models/MasterAdmin/marketingEmail';
import { DEFAULT_FROM_EMAILS } from '../../../models/MasterAdmin/marketingEmail';
import {
  deleteContact,
  fetchContacts,
  fetchSettings,
  removeSignatureImage,
  updateSettings,
  uploadSignatureImages,
} from '../../../services/MasterAdmin/marketingEmailService';
import {
  meCardClass,
  meGhostBtnClass,
  meInputClass,
  meLabelClass,
  meMainPanelClass,
  mePanelHeaderClass,
  mePanelTitleClass,
  mePrimaryBtnClass,
  meSecondaryBtnClass,
  meTabBarClass,
  meTabClass,
} from './marketingEmailUi';
import { downloadRecipientTemplate } from './downloadRecipientTemplate';

type SettingsTab = 'general' | 'images' | 'contacts';

interface EmailSettingsPanelProps {
  signatures: EmailSignature[];
  onManageSignatures: () => void;
  onClose: () => void;
}

const EmailSettingsPanel: React.FC<EmailSettingsPanelProps> = ({
  signatures,
  onManageSignatures,
  onClose,
}) => {
  const [tab, setTab] = useState<SettingsTab>('general');
  const [settings, setSettings] = useState<MarketingEmailSettings | null>(null);
  const [fromEmailsText, setFromEmailsText] = useState('');
  const [defaultFrom, setDefaultFrom] = useState('');
  const [contacts, setContacts] = useState<MarketingEmailContact[]>([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [contactSearch, setContactSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const loadSettings = useCallback(async () => {
    const res = await fetchSettings();
    setSettings(res.data);
    setDefaultFrom(res.data.defaultFromEmail);
    setFromEmailsText((res.data.fromEmails || DEFAULT_FROM_EMAILS).join('\n'));
  }, []);

  const loadContacts = useCallback(async (search = '') => {
    const res = await fetchContacts({ search, limit: 200 });
    setContacts(res.data.contacts);
    setContactsTotal(res.data.total);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await Promise.all([loadSettings(), loadContacts()]);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Failed to load settings');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadSettings, loadContacts]);

  const handleSaveGeneral = async () => {
    setSaving(true);
    try {
      const fromEmails = fromEmailsText
        .split(/[\n,;]+/)
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean);
      const res = await updateSettings({
        defaultFromEmail: defaultFrom.trim().toLowerCase(),
        fromEmails: fromEmails.length ? fromEmails : DEFAULT_FROM_EMAILS,
      });
      setSettings(res.data);
      toast.success('Settings saved');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const res = await uploadSignatureImages(Array.from(files));
      setSettings(res.data);
      toast.success(res.message || 'Images uploaded');
    } catch (e) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(msg || (e instanceof Error ? e.message : 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async (imageId: string) => {
    try {
      const res = await removeSignatureImage(imageId);
      setSettings(res.data);
      toast.success('Image removed');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to remove image');
    }
  };

  const handleDeleteContact = async (id: string) => {
    try {
      await deleteContact(id);
      await loadContacts(contactSearch);
      toast.success('Contact removed');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to remove contact');
    }
  };

  const copyImageHtml = (url: string, name: string) => {
    const html = `<img src="${url}" alt="${name}" style="max-width:120px;height:auto;" />`;
    navigator.clipboard.writeText(html);
    toast.success('Image HTML copied — paste into signature editor');
  };

  return (
    <div className={meMainPanelClass}>
      <div className={mePanelHeaderClass}>
        <div className="flex items-center gap-2">
          <Settings className="h-4 w-4 text-gray-500" />
          <h2 className={mePanelTitleClass}>Email settings</h2>
        </div>
        <button type="button" onClick={onClose} className={meGhostBtnClass}>
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="border-b border-gray-200/80 px-5 py-3 dark:border-gray-800">
        <div className={meTabBarClass}>
          {(
            [
              ['general', 'General', Settings],
              ['images', 'Signature images', ImagePlus],
              ['contacts', 'Saved emails', Users],
            ] as const
          ).map(([id, label, Icon]) => (
            <button key={id} type="button" onClick={() => setTab(id)} className={meTabClass(tab === id)}>
              <Icon className="mr-1 inline h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-5">
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-20 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
            ))}
          </div>
        ) : tab === 'general' ? (
          <div className="mx-auto max-w-2xl space-y-6">
            <div className={meCardClass + ' p-4'}>
              <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">Sender addresses</h3>
              <div className="space-y-4">
                <div>
                  <label className={meLabelClass}>Default From email</label>
                  <select
                    value={defaultFrom}
                    onChange={(e) => setDefaultFrom(e.target.value)}
                    className={meInputClass}
                  >
                    {fromEmailsText
                      .split(/[\n,;]+/)
                      .map((e) => e.trim().toLowerCase())
                      .filter(Boolean)
                      .map((email) => (
                        <option key={email} value={email}>
                          {email}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className={meLabelClass}>Allowed From emails (one per line)</label>
                  <textarea
                    value={fromEmailsText}
                    onChange={(e) => setFromEmailsText(e.target.value)}
                    rows={4}
                    className={meInputClass + ' font-mono text-xs'}
                    placeholder="tech@vedika.health&#10;marketing@vedikahealth.com"
                  />
                </div>
                <button type="button" onClick={handleSaveGeneral} disabled={saving} className={mePrimaryBtnClass}>
                  {saving ? 'Saving…' : 'Save sender settings'}
                </button>
              </div>
            </div>

            <div className={meCardClass + ' p-4'}>
              <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">Recipient import template</h3>
              <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                Download the Excel template for importing recipients. The first column should contain email addresses.
                Optional name column is ignored during import.
              </p>
              <button type="button" onClick={downloadRecipientTemplate} className={meSecondaryBtnClass}>
                <Download className="h-4 w-4" />
                Download Excel template (.xlsx)
              </button>
            </div>

            <div className={meCardClass + ' p-4'}>
              <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">Email signatures</h3>
              <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                {signatures.length} signature{signatures.length !== 1 ? 's' : ''} configured.
                Edit your Kapil Kalyani signature or add new ones.
              </p>
              <button type="button" onClick={onManageSignatures} className={meSecondaryBtnClass}>
                <PenLine className="h-4 w-4" />
                Edit signatures
              </button>
            </div>
          </div>
        ) : tab === 'images' ? (
          <div className="mx-auto max-w-3xl space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={uploading}
                onClick={() => imageInputRef.current?.click()}
                className={mePrimaryBtnClass}
              >
                <Upload className="h-4 w-4" />
                {uploading ? 'Uploading…' : 'Upload images'}
              </button>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  handleImageUpload(e.target.files);
                  e.target.value = '';
                }}
              />
              <p className="text-xs text-gray-500">Upload logo/banner images for signatures. Multiple images supported.</p>
            </div>

            {!settings?.signatureImages?.length ? (
              <div className={`${meCardClass} p-8 text-center text-sm text-gray-500`}>
                No signature images yet. Upload logos or banners to use in your email signature.
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {settings.signatureImages.map((img) => (
                  <div key={img.id} className={`${meCardClass} overflow-hidden`}>
                    <div className="flex h-32 items-center justify-center bg-white p-2 dark:bg-gray-950">
                      <img src={img.url} alt={img.name} className="max-h-full max-w-full object-contain" />
                    </div>
                    <div className="flex items-center justify-between gap-2 border-t border-gray-200/80 p-2 dark:border-gray-800">
                      <span className="truncate text-xs text-gray-600 dark:text-gray-400">{img.name}</span>
                      <div className="flex shrink-0 gap-1">
                        <button
                          type="button"
                          onClick={() => copyImageHtml(img.url, img.name)}
                          className={meGhostBtnClass + ' !px-2 !py-1 !text-xs'}
                        >
                          Copy HTML
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(img.id)}
                          className={meGhostBtnClass + ' !px-2 !py-1 text-red-500'}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="mx-auto max-w-3xl space-y-4">
            <div className="flex items-center gap-3">
              <input
                type="search"
                value={contactSearch}
                onChange={(e) => setContactSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadContacts(contactSearch)}
                placeholder="Search saved emails…"
                className={meInputClass + ' max-w-xs'}
              />
              <button type="button" onClick={() => loadContacts(contactSearch)} className={meSecondaryBtnClass}>
                Search
              </button>
              <span className="text-xs text-gray-500">{contactsTotal} saved email{contactsTotal !== 1 ? 's' : ''}</span>
            </div>

            {contacts.length === 0 ? (
              <div className={`${meCardClass} p-8 text-center text-sm text-gray-500`}>
                No saved emails yet. Emails are stored automatically when you compose or send campaigns.
              </div>
            ) : (
              <div className={`${meCardClass} divide-y divide-gray-200/80 dark:divide-gray-800`}>
                {contacts.map((c) => (
                  <div key={c.id} className="flex items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-white">{c.email}</p>
                      <p className="text-xs text-gray-500">
                        Used in {c.campaignCount} campaign{c.campaignCount !== 1 ? 's' : ''} · Last:{' '}
                        {new Date(c.lastUsedAt).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteContact(c.id)}
                      className={meGhostBtnClass + ' text-red-500'}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailSettingsPanel;
